import { useLazyQuery } from '@apollo/client';
import { LOAD_USER_AGENTS } from 'apolloClient/queries/agents';
import { LoadUserAgentsResponse } from 'apolloClient/types/Agents';
import { useAuth } from 'components/Auth/AuthProvider';
import { UnitPageContext } from 'pages/[urlParam]/[urlBuildParam]/[urlUnitParam]';
import { useContext, useEffect, useState } from 'react';
import {
  getAgentDataFromLocalStorage,
  getAgentIdFromLocalStorage,
  getLeadIdFromLocalStorage,
  getListingUnitIdentifier,
} from 'src/utils/utils';

const defaultRentPhoneEnding = '460-3368';
const defaultRentPhoneAreaCode = '786';
const defaultPhoneEnding = '697-7667';
const defaultPhoneAreaCode = '305';
const defaultSepPhoneEnding = '648-8347';
const defaultSepPhoneAreaCode = '786';

export function usePhoneNumber({
  unitPrice,
  open,
  routerPath,
}: {
  unitPrice?: number;
  open: boolean;
  routerPath?: string;
}) {
  const [phoneEnding, setPhoneEnding] = useState(defaultPhoneEnding);
  const [areaCode, setAreaCode] = useState(defaultPhoneAreaCode);
  const [loadUserAgents, { data: agentsData, loading: agentsLoading }] =
    useLazyQuery<{
      getCustomUserAgents: LoadUserAgentsResponse;
    }>(LOAD_USER_AGENTS);

  const { listing } = useContext(UnitPageContext) || {};
  const { isLogged, me } = useAuth();
  const isUnitRent =
    !!getListingUnitIdentifier(listing) &&
    listing?.propertyTypeCode === 'R' &&
    listing?.statusCode === 'A';
  useEffect(() => {
    const agentFromStorage = getAgentIdFromLocalStorage();
    const leadId = getLeadIdFromLocalStorage();

    if (isUnitRent) {
      loadUserAgents({
        variables: {
          leadId: leadId || undefined,
          agentNick: agentFromStorage?.toLowerCase() || undefined,
          listingPrice: listing?.price || undefined,
          isLogged,
          isForStorage: false,
          specificPage: getListingUnitIdentifier(listing)
            ? 'RENTAL_UNIT_PAGE'
            : 'NOT_SPECIFIC',
        },
      });
    }
  }, [isLogged, isUnitRent, listing?.price, loadUserAgents]);

  useEffect(() => {
    if (!routerPath) {
      setPhoneEnding(defaultPhoneEnding);
      setAreaCode(defaultPhoneAreaCode);
      return;
    }

    const isSearchPageForRent = routerPath?.includes('forRent=true');
    const isUnitPageForRent = routerPath?.includes('for-rent');
    const isRent = Boolean(isSearchPageForRent || isUnitPageForRent);

    // `isAgentSep` ("the lead's assigned agent is the default agent / Sep") is
    // page-independent. This hook's own query only fires on rent unit pages, so
    // fall back to the app-wide value cached by _app.tsx in localStorage.
    //
    // Priority: trust FRESH query data whenever it exists (true OR false) and only
    // read the cache when the query hasn't returned for this instance — otherwise
    // a stale cached `true` could override a fresh `false` (lead reassigned away
    // from Sep) and leak his number. No extra FUB calls either way.
    const freshAgents = agentsData?.getCustomUserAgents;
    let isAgentSep: boolean;
    if (freshAgents) {
      isAgentSep = Boolean(freshAgents.isAgentSep);
    } else {
      try {
        const cached = getAgentDataFromLocalStorage();
        isAgentSep = cached ? Boolean(JSON.parse(cached)?.isAgentSep) : false;
      } catch (error) {
        isAgentSep = false;
      }
    }

    // Sep's personal number is shown ONLY when he is genuinely the lead's agent
    // (any context). Otherwise: rent context -> rent group, else sale group.
    if (isAgentSep) {
      setPhoneEnding(defaultSepPhoneEnding);
      setAreaCode(defaultSepPhoneAreaCode);
    } else if (isRent) {
      setPhoneEnding(defaultRentPhoneEnding);
      setAreaCode(defaultRentPhoneAreaCode);
    } else {
      setPhoneEnding(defaultPhoneEnding);
      setAreaCode(defaultPhoneAreaCode);
    }
  }, [routerPath, open, agentsData]);

  return {
    phoneEnding,
    areaCode,
  };
}
